/*    
<summary>
  This functional component "ChangeAgreementPasswordForm" handles the UI and logic for setting or updating the agreement password.
  It includes validation, show/hide password toggles, and resets the form on submit.
  Developer: Sahil Bharti
</summary>
<returns>Returns JSX</returns>
*/

import { useState } from "react";
import { Form, Formik, Field } from "formik";
import { IUpdateAgreementPassword } from "../../../../models/forms/IChangePassword";
import { ChangeAgreementPasswordValidateSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import Label from "../../../../shared-components/label/label";
import { ShowHidePasswordButton } from "../../../../shared-components/Grid/custom-grid-formatter";
import LoaderButton from "../../../../shared-components/button/loader-button";
import { Col, Row } from "react-bootstrap";
import FormikFormInputForRegexCheck from "../../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import { integerRegex } from "../../../../constants/constants";
import { toHalfWidth } from "../../../../helpers/validate-helper";

interface IProps {
  id: number;
  submitHandler: (data: IUpdateAgreementPassword) => void;
  loading: boolean;
  initialValues: any;
  isUpdateMode: boolean;
  disableAction: boolean;
}

const ChangeAgreementPasswordForm = (props: IProps) => {
  const [showHideNewPassword, setShowHideNewPassword] = useState(false);
  const [showHideConfirmPassword, setShowHideConfirmPassword] = useState(false);

  return (
    <Formik
      initialValues={props.initialValues}
      enableReinitialize
      validationSchema={ChangeAgreementPasswordValidateSchema}
      validateOnBlur={false}
      onSubmit={async (values, { resetForm }) => {
        props.submitHandler({
          ...values,
          Password: toHalfWidth(values.Password),
          ConfirmPassword: toHalfWidth(values.ConfirmPassword),
        });
        resetForm();
      }}
    >
      {() => (
        <Form className="setting-form" noValidate>
          <Row className="position-relative">
            {/* New Password */}
            <Col xs={12} md={12} className="form-group">
              <Label required label="password" />
              <div className="position-relative">
                <Field
                  data-testid="testPassword"
                  className="form-control"
                  name="Password"
                  type={showHideNewPassword ? "text" : "password"}
                  placeholder="enter_new_password"
                  invalidcharacter={[" "]}
                  validcharacter={[
                    "~",
                    "`",
                    "!",
                    "@",
                    "#",
                    "$",
                    "%",
                    "^",
                    "&",
                    "*",
                    "(",
                    ")",
                    "_",
                    "-",
                    "+",
                    "=",
                    "{",
                    "[",
                    "}",
                    "]",
                    "|",
                    "\\",
                    ":",
                    ";",
                    "'",
                    '"',
                    "<",
                    ",",
                    ">",
                    ".",
                    "?",
                    "/",
                  ]}
                  regex={integerRegex}
                  as={FormikFormInputForRegexCheck}
                />
                <ShowHidePasswordButton
                  showHidePassword={showHideNewPassword}
                  setShowHidePassword={setShowHideNewPassword}
                />
              </div>
            </Col>

            {/* Confirm Password */}
            <Col xs={12} md={12} className="form-group">
              <Label required label="confirm_password" />
              <div className="position-relative">
                <Field
                  data-testid="testConfirmPassword"
                  className="form-control"
                  name="ConfirmPassword"
                  type={showHideConfirmPassword ? "text" : "password"}
                  placeholder="enter_new_password"
                  invalidcharacter={[" "]}
                  validcharacter={[
                    "~",
                    "`",
                    "!",
                    "@",
                    "#",
                    "$",
                    "%",
                    "^",
                    "&",
                    "*",
                    "(",
                    ")",
                    "_",
                    "-",
                    "+",
                    "=",
                    "{",
                    "[",
                    "}",
                    "]",
                    "|",
                    "\\",
                    ":",
                    ";",
                    "'",
                    '"',
                    "<",
                    ",",
                    ">",
                    ".",
                    "?",
                    "/",
                  ]}
                  regex={integerRegex}
                  as={FormikFormInputForRegexCheck}
                />
                <ShowHidePasswordButton
                  showHidePassword={showHideConfirmPassword}
                  setShowHidePassword={setShowHideConfirmPassword}
                />
              </div>
            </Col>

            {/* Save Button */}
            <Col xs={12} md={12}>
              <LoaderButton
                type="submit"
                className="mt-3"
                iconName="bi-check-lg"
                isLoading={props.loading}
                disabled={props.disableAction}
                text={props.isUpdateMode ? "button_update" : "button_save"}
              />
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default ChangeAgreementPasswordForm;
